/*
 * Section admin : rapport hebdomadaire par email.
 * Chaque lundi, un recapitulatif des 7 derniers jours est envoye au
 * destinataire configure. Compteurs agreges uniquement, aucune donnee
 * de sante ni d'identite de prospecte.
 */

import { useState } from "react";
import { CalendarClock, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { adminRunWeeklyReport } from "@/lib/api";

const sectionClass =
  "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-5";

export const WeeklyReportSection = ({ weeklyReport, onPatch, adminKey }) => {
  const [sending, setSending] = useState(false);

  const sendTest = async () => {
    setSending(true);
    try {
      const result = await adminRunWeeklyReport(adminKey);
      if (result.status === "sent") {
        toast.success("Rapport envoyé", {
          description: `Un rapport de test vient de partir vers ${weeklyReport.recipient}.`,
        });
      } else if (result.status === "disabled") {
        toast.error("Rapport désactivé", {
          description: "Active le rapport puis enregistre avant d'envoyer un test.",
        });
      } else {
        toast.error("Envoi impossible", {
          description: "Vérifie l'adresse du destinataire puis réessaie.",
        });
      }
    } catch (e) {
      toast.error("Erreur", { description: "L'envoi du test a échoué. Réessaie." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className={sectionClass} data-testid="admin-weekly-report-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#235277]">
            <CalendarClock className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-heading text-base font-semibold text-[#060606]">
              Rapport hebdomadaire
            </h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
            Chaque lundi, recevez par email le récapitulatif des 7 derniers jours :
            parcours, bilans, rappels et rendez vous. Compteurs anonymes uniquement,
            jamais de données de santé.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="weekly-report-enabled" className="text-xs font-medium text-[#333333]">
            {weeklyReport.enabled ? "Activé" : "Désactivé"}
          </Label>
          <Switch
            id="weekly-report-enabled"
            data-testid="admin-weekly-report-enabled-switch"
            checked={weeklyReport.enabled}
            onCheckedChange={(value) => onPatch("enabled", Boolean(value))}
            className="data-[state=checked]:bg-[#235277]"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <Label htmlFor="weekly-report-recipient" className="text-sm font-medium text-[#060606]">
            Adresse du destinataire
          </Label>
          <Input
            id="weekly-report-recipient"
            type="email"
            data-testid="admin-weekly-report-recipient-input"
            value={weeklyReport.recipient}
            onChange={(e) => onPatch("recipient", e.target.value)}
            placeholder="contact@ginobodyandsoul.com"
            className="mt-1.5 min-h-[44px] rounded-xl border-[rgba(35,82,119,0.18)]"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={sending}
          onClick={sendTest}
          data-testid="admin-weekly-report-test-button"
          className="min-h-[44px] rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-medium transition-colors duration-200"
        >
          <Send className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
          {sending ? "Envoi en cours" : "Envoyer un test"}
        </Button>
      </div>
      <p className="text-[11px] leading-relaxed text-[#9E9E9E]">
        Le test part immédiatement vers le destinataire enregistré. Pense à enregistrer
        les paramètres si tu viens de modifier l'adresse.
      </p>
    </section>
  );
};
